import type { ReactNode } from "react";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useLocation, useNavigate } from "react-router-dom";
import { InternalPage } from "../../../components/primitives/layout/InternalPage";
import { PageHeader, type PageBreadcrumb } from "../../../components/primitives/layout/PageHeader";
import { gaps } from "../../../components/primitives/spacing";
import { ROUTES } from "../../../route_paths";

interface LibrarySubtab {
  to: string;
  label: string;
}

interface LibraryTab {
  /** The path every page of the tab sits under. */
  to: string;
  label: string;
  /** The pages of the tab, shown as a button group under the tabs; the first is where the tab opens. */
  subtabs?: LibrarySubtab[];
}

const { library } = ROUTES.internal;

const TABS: LibraryTab[] = [
  { to: library.feeds.path(), label: "Feeds" },
  {
    to: library.catalogue.path(),
    label: "Catalogue",
    subtabs: [
      { to: library.catalogue.fields.path(), label: "Health fields" },
      { to: library.catalogue.subjects.path(), label: "Subjects" },
      { to: library.catalogue.documents.path(), label: "Documents" },
      { to: library.catalogue.rejected.path(), label: "Rejected" },
    ],
  },
  {
    to: library.coverage.path(),
    label: "Coverage",
    subtabs: [
      { to: library.coverage.gaps.path(), label: "Coverage gaps" },
      { to: library.coverage.uncoveredClaims.path(), label: "Uncovered claims" },
    ],
  },
  {
    to: library.taxonomy.path(),
    label: "Taxonomy",
    subtabs: [{ to: library.taxonomy.gaps.path(), label: "Gaps" }],
  },
];

interface LibraryPageProps {
  /** The page's primary actions, shown at the end of the heading. */
  actions?: ReactNode;
  /** For a page nested in a tab (a feed in Feeds): the page's own title, which turns the heading
   *  into a trail, "Library › … › title". Without it the heading is "Library". */
  title?: ReactNode;
  /** The pages between Library and `title`, outermost first. */
  parents?: PageBreadcrumb[];
  children: ReactNode;
}

/** Shell of every /internal/library/* page: the Library heading, its tabs, then the page's content.
 *  The gap is tighter than other internal pages, since the tabs sit directly on what they switch. */
export function LibraryPage({ actions, title, parents = [], children }: LibraryPageProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tab = TABS.find((candidate) => pathname.startsWith(candidate.to));
  const subtab = tab?.subtabs?.find((candidate) => pathname.startsWith(candidate.to));

  return (
    <InternalPage width="full" gap={gaps.base}>
      {title === undefined ? (
        <PageHeader
          title="Library"
          subtitle="What the corpus knows, and what keeps it up to date."
          actions={actions}
        />
      ) : (
        <PageHeader
          title={title}
          breadcrumbs={[{ label: "Library", to: ROUTES.internal.library.path() }, ...parents]}
          actions={actions}
        />
      )}
      <Tabs
        value={tab?.to ?? false}
        onChange={(_e, value: string) => {
          const next = TABS.find((candidate) => candidate.to === value);
          navigate(next?.subtabs?.[0].to ?? value);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {TABS.map((candidate) => (
          <Tab key={candidate.to} value={candidate.to} label={candidate.label} />
        ))}
      </Tabs>
      {tab?.subtabs ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          aria-label={`${tab.label} pages`}
          sx={{ alignSelf: "flex-start" }}
          value={subtab?.to ?? null}
          onChange={(_e, value: string | null) => {
            if (value !== null) navigate(value);
          }}
        >
          {tab.subtabs.map((candidate) => (
            <ToggleButton key={candidate.to} value={candidate.to}>
              {candidate.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}
      {children}
    </InternalPage>
  );
}
